'use client';

/**
 * **A boolean row as a live control rather than a read-out you have to open**
 * (LF-6303 published the vocabulary, LF-6350 / [ATTR-8] picks and builds one).
 *
 * `BooleanControl` was published by [ATTR-2] "ahead of its selector", with the
 * note that *which* of the four the Contacts drawer uses is [ATTR-5]'s call.
 * [ATTR-5] shipped `select` — the two-option dropdown, reached through the same
 * branch a real `select` takes — and story 7a, the epic's chosen direction,
 * renders `segmented`. This is that difference closed: story 7a's own control,
 * ported from `components/prototypes/form-system/attribute-editors.tsx`.
 *
 * ## Why `segmented` and not the other two live options
 *
 * · **`switch`** is smaller and the most obviously interactive, but a switch is a
 *   *control*, so a card of them reads less like a record and more like a
 *   settings page — against the whole argument of the treatment. It also ships at
 *   24×44 with a hard-coded thumb and no size variant, i.e. **taller than the
 *   value cell**, so a card of them breaks the row rhythm the grid depends on.
 * · **`checkbox`** is what Attio uses and the smallest of the three, and it
 *   carries exactly the ambiguity the `Yes` / `No` pair exists to avoid: *an
 *   unticked box is indistinguishable from a question nobody answered*, whereas
 *   `Verified — No` states it. It is separately §5-rejected for giving the value
 *   column a ragged left edge, which is the one thing the fixed label column is
 *   for.
 *
 * `segmented` states the value **in words**, so at rest the row still reads as a
 * fact rather than as a switch position. It costs roughly twice the width of a
 * badge, which the two-column drawer has.
 *
 * ## This is now the default (LF-6388 / [ATTR-10])
 *
 * `booleanControl` defaulted to `select` while [ATTR-8] was *adding* a control
 * rather than changing one, so that no shipped pixel outside Contacts moved.
 * [ATTR-10] flipped the default to `segmented`, and the flip is free: **every**
 * `kind: 'boolean'` spec in production is Contacts' — all six live in
 * `contact-attribute-specs.ts`, and `lead-field-specs.ts` declares none at all
 * — so the `select` default was guarding a case that does not exist. Checked by
 * grep rather than assumed.
 *
 * The prop stays, because the four-value vocabulary is real; what changed is
 * which arm a surface gets without having to ask for it.
 */

import { useLayoutEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { AttributeSegmentedBoolean } from './attribute-segmented-boolean';
import {
  ATTRIBUTE_INSET,
  ATTRIBUTE_LEADING,
  ATTRIBUTE_VALUE_SLOT
} from './attribute-tokens';
import type { BooleanControl, AttributeSpec } from './attribute-types';

/**
 * **The arms of {@link BooleanControl} that actually exist**, as opposed to the
 * four the vocabulary publishes.
 *
 * `Extract` rather than a re-typed pair of literals, so the two cannot drift: if
 * `BooleanControl` ever renames or drops an arm this stops compiling instead of
 * quietly describing a control nobody implemented. The other two (`switch`,
 * `checkbox`) stay in the published union as the alternatives that were
 * considered and rejected — see the header for why — and a grid prop that offered
 * them would be a name promising something it cannot render.
 */
export type BooleanControlSetting = Extract<
  BooleanControl,
  'select' | 'segmented'
>;

/**
 * Whether a row renders as a live control instead of a read-out + editor pair.
 *
 * The one predicate both the grid's render and its keyboard chain read, so
 * "is this row live?" has a single answer rather than two that can disagree.
 *
 * **A `readOnly` row is never live** (VALE-1699). This predicate used to answer
 * on the editor kind alone, so a read-only boolean drew its `Yes | No` pair
 * anyway: two real buttons, focusable, in the Tab chain, and wired to a commit
 * the row had already refused. That is the exact lie `AttributeReadOut` is built
 * to avoid one branch over — it renders a `<span>` rather than a `<button>`
 * specifically so "the element itself carries the claim" — and the boolean arm
 * simply never asked the question, because until there was a read-only review
 * surface no consumer put the two together.
 *
 * Found on the enquiry Intake tab, where every row is read-only by design and
 * `LPR` and `Beneficiary` were the only two still offering a control. With this,
 * they fall through to the read-out and draw `Yes` / `No` as text.
 */
export function isLiveBoolean(
  field: AttributeSpec<unknown>,
  control: BooleanControlSetting
): boolean {
  if (field.readOnly) return false;
  return field.editor.kind === 'boolean' && control !== 'select';
}

export function AttributeBooleanSegmented({
  field,
  focused,
  onToggle,
  onStep
}: {
  field: AttributeSpec<unknown>;
  /** True while the grid's `Tab` chain is on this row. */
  focused: boolean;
  /** Commits the flip. Routed through the grid's one commit path, never around it. */
  onToggle: (next: boolean) => void;
  /** Moves the grid's `Tab` chain, so a live row is a stop rather than a hole. */
  onStep: (delta: number) => void;
}) {
  const checked = field.draft === 'yes';
  const ref = useRef<HTMLDivElement>(null);

  /*
    Focus whatever this rendered when the chain lands here. Without it a
    `segmented` row is a gap in the Tab order — the grid's chain addresses rows
    by index and there is no editor to mount and autofocus on a live one.
  */
  useLayoutEffect(() => {
    if (!focused) return;
    ref.current?.querySelector<HTMLElement>('button,input')?.focus();
  }, [focused]);

  return (
    <div
      ref={ref}
      onKeyDown={(event) => {
        if (event.key !== 'Tab') return;
        event.preventDefault();
        onStep(event.shiftKey ? -1 : 1);
      }}
      /*
        The same inset and leading every other value cell carries, so a card of
        mixed field kinds keeps one rhythm and the control's first line sits on
        the label's — `ATTRIBUTE_INSET` is what the read-out and the open editor
        both use, and a bespoke padding here is what would shift this row.
      */
      className={cn(
        'items-center gap-2',
        ATTRIBUTE_VALUE_SLOT,
        ATTRIBUTE_INSET,
        ATTRIBUTE_LEADING
      )}
    >
      {/*
        The control itself lives in `./attribute-segmented-boolean` as of LF-6314,
        so the inventory drawer's "Confirmed against a source" — a boolean that
        belongs to a figure rather than to a record, and so has no `AttributeSpec`
        — renders the same one rather than a second copy of its geometry. What
        stays here is the grid plumbing: the `Tab` chain, the focus-on-arrival, and
        the single commit path.
      */}
      {/*
        `valueClassName` goes to the SEGMENTS, which are the only part of this
        control that stands for the value (VALE-1208): a segment sets its own
        `data-[state=on]:text-foreground`, so a class on the track never reaches
        the lit answer. `valueWrapper` then hangs whatever the caller needs
        around the control; this row never reads `display`, so it is the only
        way in.
      */}
      {/*
        `markWrapper` inside `valueWrapper` (VALE-1264): the outer one wraps the
        row's whole target, the inner one just the control. A caller hanging a
        hover popover uses the inner, so the segments keep their own hit areas.
      */}
      {(field.valueWrapper ?? ((node: React.ReactNode) => node))(
        (field.markWrapper ?? ((node: React.ReactNode) => node))(
          <AttributeSegmentedBoolean
            value={checked}
            onValueChange={onToggle}
            ariaLabel={field.label}
            segmentClassName={field.valueClassName}
          />
        )
      )}
      {/*
        **The row's badge, which the read-out path renders and this one did
        not** (VALE-1208).

        `AttributeSpec.badge` is documented as "a second, smaller fact rendered
        beside the value", and every text row honours it through
        `AttributeReadOut`. A segmented boolean renders its own control instead,
        so a badge set on such a row simply never appeared, which is how the
        AI-extract review's `NEW` / `UPDATED` mark went missing on exactly the
        fields whose value is a control rather than text.
      */}
      {field.badge != null && field.badge}
    </div>
  );
}

/**
 * The tri-state's counterpart to {@link isLiveBoolean}.
 *
 * Unconditional, where the boolean's is gated on `booleanControl`: a `triState`
 * has no dropdown arm to fall back to. The whole reason the kind exists is that
 * the dropdown was the wrong instrument for it — see the union's own note.
 */
export function isLiveTriState(field: AttributeSpec<unknown>): boolean {
  /* A read-only row is never live — see {@link isLiveBoolean} (VALE-1699). */
  if (field.readOnly) return false;
  return field.editor.kind === 'triState';
}

/**
 * **The grid plumbing for a `triState` row** — the sibling of
 * {@link AttributeBooleanSegmented}, and deliberately the same shape.
 *
 * Everything structural is shared with the boolean: the `Tab` chain, the
 * focus-on-arrival, the inset/leading that keeps a card of mixed field kinds on
 * one rhythm, and the single commit path. What differs is only the value space —
 * three stored members instead of two, mapped onto the presentational control's
 * `boolean | null` — so `AttributeSegmentedBoolean` renders both and its pill
 * geometry has one definition.
 */
export function AttributeTriStateSegmented({
  field,
  focused,
  onCommit,
  onStep
}: {
  field: AttributeSpec<unknown>;
  /** True while the grid's `Tab` chain is on this row. */
  focused: boolean;
  /** Commits the answer. Routed through the grid's one commit path, never around it. */
  onCommit: (next: string) => void;
  /** Moves the grid's `Tab` chain, so a live row is a stop rather than a hole. */
  onStep: (delta: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const editor = field.editor as Extract<
    AttributeSpec<unknown>['editor'],
    { kind: 'triState' }
  >;

  /*
    Anything that is not one of the two answers reads as unanswered, rather than
    `=== unsetValue`. A row whose column holds a legacy or unexpected value then
    renders as "not answered yet" — which is true, and is offered both answers —
    instead of silently lighting a segment that does not match what is stored.
  */
  const value =
    field.draft === editor.yes.value
      ? true
      : field.draft === editor.no.value
        ? false
        : null;

  // Same reason as the boolean's: the chain addresses rows by index and a live
  // row mounts no editor to autofocus, so without this it is a gap in Tab order.
  useLayoutEffect(() => {
    if (!focused) return;
    ref.current?.querySelector<HTMLElement>('button,input')?.focus();
  }, [focused]);

  return (
    <div
      ref={ref}
      onKeyDown={(event) => {
        if (event.key !== 'Tab') return;
        event.preventDefault();
        onStep(event.shiftKey ? -1 : 1);
      }}
      className={cn(
        'items-center gap-2',
        ATTRIBUTE_VALUE_SLOT,
        ATTRIBUTE_INSET,
        ATTRIBUTE_LEADING
      )}
    >
      {/* The same three slots the boolean honours, see its note (VALE-1208). */}
      {(field.valueWrapper ?? ((node: React.ReactNode) => node))(
        (field.markWrapper ?? ((node: React.ReactNode) => node))(
          <AttributeSegmentedBoolean
            value={value}
            onValueChange={(next) =>
              onCommit(next ? editor.yes.value : editor.no.value)
            }
            onUnset={() => onCommit(editor.unsetValue)}
            ariaLabel={field.label}
            yesLabel={editor.yes.label}
            noLabel={editor.no.label}
            segmentClassName={field.valueClassName}
          />
        )
      )}
      {field.badge != null && field.badge}
    </div>
  );
}
